<template>
  <div
    class="ext-focus-items"
    :style="embedded
      ? 'background:#fff; overflow:hidden;'
      : 'margin-bottom:24px; background:#fff; border:1px solid #F2EEE8; border-radius:8px; overflow:hidden; padding:12px 14px;'"
  >
    <!-- Header / action bar. When a `title` prop is given (embedded on /engage),
         the component renders its OWN header row — title left, buttons right —
         as one flush unit with the panel below (no ConfigurablePage panel, no
         double chrome). On /external, no title: the host page supplies the
         header and only the buttons render, right-aligned. -->
    <div
      :class="{ 'mb-3': !title }"
      :style="`display:flex; align-items:center; gap:8px; ${title ? 'justify-content:space-between; padding-bottom:12px;' : 'justify-content:flex-end;'}`"
    >
      <h2 v-if="title" style="font-size:15px; font-weight:600; color:#1F2937; margin:0;">{{ title }}</h2>
      <div style="display:flex; align-items:center; gap:8px;">
        <!-- Archived toggle — Admin-only. standard/portal/restricted users
             never see this control (and the API hides archived rows from
             them regardless). -->
        <button
          v-if="canViewArchived"
          @click="toggleArchivedView"
          style="display:inline-flex; align-items:center; gap:5px; padding:6px 12px; font-size:12px; font-weight:500; border-radius:6px; cursor:pointer; transition:all 0.15s;"
          :style="showArchived
            ? 'color:#374151; background:#F3F4F6; border:1px solid #D1D5DB;'
            : 'color:#6B7280; background:#fff; border:1px solid #D1D5DB;'"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="5" rx="1"/><path d="M4 9v9a2 2 0 002 2h12a2 2 0 002-2V9"/><line x1="10" y1="13" x2="14" y2="13"/></svg>
          {{ showArchived ? 'Archived' : 'Show Archived' }}
        </button>
        <button
          v-if="canEdit && !showCreate && !showArchived"
          @click="showCreate = true"
          style="display:inline-flex; align-items:center; gap:5px; padding:6px 14px; font-size:12px; font-weight:500; color:#4B5563; background:#fff; border:1px solid #D1D5DB; border-radius:6px; cursor:pointer; transition:all 0.15s;"
          @mouseenter="$event.target.style.borderColor='#9CA3AF'; $event.target.style.background='#F9FAFB'"
          @mouseleave="$event.target.style.borderColor='#D1D5DB'; $event.target.style.background='#fff'"
        >
          <span style="font-size:14px; line-height:1;">+</span> Add Focus Item
        </button>
      </div>
    </div>

    <!-- Create form (inline, above the widget) -->
    <div v-if="showCreate" style="border:1px dashed #D97706; background:#FFFBF5; border-radius:8px; padding:14px 16px; margin-bottom:12px;">
      <div style="display:flex; gap:10px; align-items:flex-start;">
        <div style="flex:1; display:flex; flex-direction:column; gap:8px;">
          <input
            ref="createTitle"
            v-model="newItem.title"
            placeholder="What should operators focus on?"
            style="width:100%; padding:8px 10px; border:1px solid #E5E7EB; border-radius:6px; font-size:13px; color:#1F2937; background:#fff; outline:none;"
            @focus="$event.target.style.borderColor='#D97706'"
            @blur="$event.target.style.borderColor='#E5E7EB'"
            @keydown.enter.prevent="createItem"
          />
          <textarea
            v-model="newItem.description"
            placeholder="Add detail or context (optional)"
            rows="2"
            style="width:100%; padding:8px 10px; border:1px solid #E5E7EB; border-radius:6px; font-size:12px; color:#374151; background:#fff; resize:vertical; outline:none; line-height:1.5;"
            @focus="$event.target.style.borderColor='#D97706'"
            @blur="$event.target.style.borderColor='#E5E7EB'"
          />
          <div style="display:flex; gap:8px; align-items:center;">
            <select
              v-model="newItem.priority"
              style="padding:5px 8px; border:1px solid #E5E7EB; border-radius:5px; font-size:11px; color:#374151; background:#fff;"
            >
              <option value="p0">Critical</option>
              <option value="p1">High</option>
              <option value="p2">Medium</option>
              <option value="p3">Low</option>
            </select>
            <select
              v-if="currentEntity || childEntities.length"
              v-model="newItem.entity"
              style="padding:5px 8px; border:1px solid #E5E7EB; border-radius:5px; font-size:11px; color:#374151; background:#fff; max-width:260px;"
            >
              <option v-if="currentEntity" :value="currentEntity.id">{{ currentEntity.name }}</option>
              <option :value="null">All (ACO-wide)</option>
              <option v-for="e in childEntities" :key="e.id" :value="e.id">{{ e.name }}</option>
            </select>
          </div>
        </div>
      </div>
      <div style="display:flex; gap:8px; justify-content:flex-end; margin-top:10px;">
        <button
          @click="cancelCreate"
          style="padding:5px 12px; font-size:12px; color:#6B7280; background:#fff; border:1px solid #D1D5DB; border-radius:5px; cursor:pointer;"
        >Cancel</button>
        <button
          @click="createItem"
          :disabled="!newItem.title.trim() || saving"
          style="padding:5px 14px; font-size:12px; font-weight:500; color:#fff; background:#D97706; border:none; border-radius:5px; cursor:pointer; opacity:1; transition:opacity 0.15s;"
          :style="{ opacity: (!newItem.title.trim() || saving) ? 0.5 : 1 }"
        >{{ saving ? 'Saving…' : 'Add' }}</button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loadingItems" :style="`display:flex; align-items:center; justify-content:center; padding:48px 0; color:#A9A29A; font-size:12px; border-top:1px solid #F2EEE8; margin:${innerPanelMargin};`">
      Loading…
    </div>

    <!-- Empty state -->
    <div v-else-if="!visibleItems.length" :style="`padding:16px 20px; color:#A9A29A; font-size:12px; border-top:1px solid #F2EEE8; margin:${innerPanelMargin};`">
      {{ showArchived ? 'No archived focus areas.' : 'No focus areas suggested.' }}
    </div>

    <!-- Two-panel widget: list on the left, detail on the right. Border chrome
         now lives on the outer .ext-focus-items wrapper so it encloses the
         action bar + this panel as one rounded unit. -->
    <div v-else :style="`display:flex; background:#fff; border-top:1px solid #F2EEE8; overflow:hidden; height:380px; margin:${innerPanelMargin};`">
      <!-- Left panel: list -->
      <div style="width:320px; flex-shrink:0; border-right:1px solid #F2EEE8; overflow-y:auto; height:100%;">
        <div
          v-for="item in visibleItems"
          :key="item.id"
          class="focus-list-row"
          :class="{ 'focus-list-row--active': selectedId === item.id, 'focus-list-row--done': isCompleted(item) }"
          @click="selectItem(item)"
        >
          <div style="display:flex; align-items:center; gap:6px; margin-bottom:3px;">
            <!-- Completed rows show a check instead of the priority dot -->
            <svg v-if="isCompleted(item)" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16A34A" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><polyline points="20 6 9 17 4 12"/></svg>
            <span
              v-else
              style="display:inline-block; width:7px; height:7px; border-radius:50%; flex-shrink:0;"
              :style="{ background: priorityColor(item.priority, item.status) }"
            />
            <span
              style="font-size:12.5px; font-weight:600; line-height:1.3; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; flex:1;"
              :style="{
                color: isCompleted(item) ? '#9CA3AF' : (item.status === 'archived' ? '#9CA3AF' : '#1F2937'),
                textDecoration: isCompleted(item) ? 'line-through' : 'none',
              }"
            >
              {{ item.title }}
            </span>
            <!-- Comment indicator — filled brand pill so it stands out. -->
            <span
              v-if="itemCommentCount(item) > 0"
              style="display:inline-flex; align-items:center; gap:3px; flex-shrink:0; color:#fff; background:#D97706; font-size:10px; font-weight:600; padding:1px 6px; border-radius:9px; line-height:1.4;"
              :title="`${itemCommentCount(item)} comment${itemCommentCount(item) === 1 ? '' : 's'}`"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
              {{ itemCommentCount(item) }}
            </span>
          </div>
          <div style="display:flex; align-items:center; gap:6px; margin-left:13px;">
            <span class="sensitive" v-if="item.entity_name" style="font-size:10px; color:#9CA3AF; background:#F3F4F6; padding:1px 6px; border-radius:3px; flex-shrink:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:140px;">
              {{ item.entity_name }}
            </span>
            <span style="font-size:10px; color:#9CA3AF;">{{ priorityLabel(item.priority) }}</span>
            <span v-if="item.due_date" style="font-size:10px;" :style="{ color: isOverdue(item) ? '#DC2626' : '#9CA3AF' }">
              Due {{ formatDate(item.due_date) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Right panel: detail -->
      <div style="flex:1; overflow-y:auto; height:100%; padding:20px 24px;">
        <template v-if="selectedItem">
          <!-- Edit mode -->
          <div v-if="editingId === selectedItem.id">
            <div style="display:flex; flex-direction:column; gap:8px;">
              <input
                v-model="editForm.title"
                style="width:100%; padding:6px 10px; border:1px solid #E5E7EB; border-radius:5px; font-size:14px; font-weight:600; color:#1F2937; outline:none;"
                @keydown.enter.prevent="saveEdit"
                @keydown.escape="cancelEdit"
              />
              <textarea
                v-model="editForm.description"
                rows="3"
                style="width:100%; padding:6px 10px; border:1px solid #E5E7EB; border-radius:5px; font-size:12px; color:#374151; resize:vertical; outline:none; line-height:1.5;"
                @keydown.escape="cancelEdit"
              />
              <div style="display:flex; gap:8px; align-items:center;">
                <select v-model="editForm.priority" style="padding:4px 8px; border:1px solid #E5E7EB; border-radius:5px; font-size:11px; color:#374151;">
                  <option value="p0">Critical</option>
                  <option value="p1">High</option>
                  <option value="p2">Medium</option>
                  <option value="p3">Low</option>
                </select>
                <select
                  v-if="currentEntity || childEntities.length"
                  v-model="editForm.entity"
                  style="padding:4px 8px; border:1px solid #E5E7EB; border-radius:5px; font-size:11px; color:#374151; max-width:260px;"
                >
                  <option v-if="currentEntity" :value="currentEntity.id">{{ currentEntity.name }}</option>
                  <option :value="null">All (ACO-wide)</option>
                  <option v-for="e in childEntities" :key="e.id" :value="e.id">{{ e.name }}</option>
                </select>
              </div>
              <div style="display:flex; gap:8px; justify-content:flex-end;">
                <button @click="cancelEdit" style="padding:4px 10px; font-size:11px; color:#6B7280; background:#fff; border:1px solid #D1D5DB; border-radius:5px; cursor:pointer;">Cancel</button>
                <button @click="saveEdit" :disabled="!editForm.title.trim() || saving" style="padding:4px 12px; font-size:11px; font-weight:500; color:#fff; background:#D97706; border:none; border-radius:5px; cursor:pointer;" :style="{ opacity: saving ? 0.5 : 1 }">Save</button>
              </div>
            </div>
          </div>

          <!-- Read mode -->
          <div v-else>
            <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:4px;">
              <div style="display:flex; align-items:center; gap:8px; flex:1; min-width:0;">
                <span
                  style="display:inline-block; width:9px; height:9px; border-radius:50%; flex-shrink:0;"
                  :style="{ background: priorityColor(selectedItem.priority, selectedItem.status) }"
                />
                <span style="font-size:16px; font-weight:600; color:#1F2937; line-height:1.3;">{{ selectedItem.title }}</span>
                <span class="sensitive" v-if="selectedItem.entity_name" style="font-size:10px; color:#9CA3AF; background:#F3F4F6; padding:2px 7px; border-radius:3px; flex-shrink:0;">
                  {{ selectedItem.entity_name }}
                </span>
              </div>
              <div style="display:flex; gap:4px; flex-shrink:0;">
                <!-- Archived items: only Unarchive / Edit / Delete make sense
                     (they're already off the active workflow). -->
                <template v-if="selectedItem.status === 'archived'">
                  <button v-if="canManage" @click="unarchiveItem(selectedItem)" class="focus-icon-btn" title="Unarchive">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="5" rx="1"/><path d="M4 9v9a2 2 0 002 2h12a2 2 0 002-2V9"/><polyline points="9 15 12 12 15 15"/><line x1="12" y1="12" x2="12" y2="17"/></svg>
                  </button>
                </template>
                <template v-else>
                  <button v-if="canAct" @click="markDone(selectedItem)" class="focus-icon-btn" title="Mark complete">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                  <button v-if="canAct" @click="markNotRelevant(selectedItem)" class="focus-icon-btn" title="Mark not relevant">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="4.9" y1="4.9" x2="19.1" y2="19.1"/></svg>
                  </button>
                  <button v-if="canManage" @click="archiveItem(selectedItem)" class="focus-icon-btn" title="Archive">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="5" rx="1"/><path d="M4 9v9a2 2 0 002 2h12a2 2 0 002-2V9"/><line x1="10" y1="13" x2="14" y2="13"/></svg>
                  </button>
                </template>
                <button v-if="canManage" @click="startEdit(selectedItem)" class="focus-icon-btn" title="Edit">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button v-if="canManage" @click="deleteItem(selectedItem)" class="focus-icon-btn" title="Remove" style="color:#DC2626;">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>

            <div style="display:flex; gap:10px; margin-left:17px; margin-bottom:12px;">
              <span style="font-size:11px; color:#9CA3AF;">{{ priorityLabel(selectedItem.priority) }}</span>
              <span v-if="selectedItem.due_date" style="font-size:11px;" :style="{ color: isOverdue(selectedItem) ? '#DC2626' : '#9CA3AF' }">
                Due {{ formatDate(selectedItem.due_date) }}
              </span>
            </div>

            <div v-if="selectedItem.description" style="font-size:13px; color:#374151; line-height:1.6; white-space:pre-line; margin-left:17px; margin-bottom:16px;">
              {{ selectedItem.description }}
            </div>
            <div v-else style="font-size:12px; color:#A9A29A; margin-left:17px; margin-bottom:16px;">
              No description.
            </div>

            <!-- Screenshots -->
            <div v-if="imageAttachments(selectedItem).length" style="margin-left:17px; margin-bottom:16px;">
              <div style="font-size:12px; font-weight:500; color:#6B7280; margin-bottom:8px; display:flex; align-items:center; gap:5px;">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/></svg>
                Screenshots ({{ imageAttachments(selectedItem).length }})
              </div>
              <div style="display:flex; flex-wrap:wrap; gap:8px;">
                <button
                  v-for="a in imageAttachments(selectedItem)"
                  :key="a.id"
                  class="focus-shot-thumb"
                  :title="a.filename"
                  @click="openAttachment(a)"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/></svg>
                  <span style="font-size:10px; color:#6B7280; max-width:88px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{{ a.filename }}</span>
                </button>
              </div>
            </div>

            <!-- Comments -->
            <div style="margin-left:17px; border-top:1px solid #F2EEE8; padding-top:12px;">
              <div style="font-size:12px; font-weight:500; color:#6B7280; margin-bottom:8px; display:flex; align-items:center; gap:5px;">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                Comments<template v-if="commentCount(selectedItem) != null"> ({{ commentCount(selectedItem) }})</template>
              </div>

              <div v-if="commentLoading[selectedItem.id]" style="font-size:11px; color:#9CA3AF;">Loading…</div>
              <template v-else>
                <div v-if="(commentsByItem[selectedItem.id] || []).length" style="display:flex; flex-direction:column; gap:10px; margin-bottom:10px;">
                  <div v-for="c in commentsByItem[selectedItem.id]" :key="c.id" style="font-size:12px;">
                    <div style="display:flex; gap:8px; align-items:baseline;">
                      <span style="font-weight:600; color:#374151;">{{ commentAuthor(c) }}</span>
                      <span style="font-size:10px; color:#9CA3AF;">{{ formatCommentTime(c) }}</span>
                    </div>
                    <div style="color:#4B5563; line-height:1.5; white-space:pre-line;">{{ c.body }}</div>
                  </div>
                </div>
                <div v-else style="font-size:11px; color:#9CA3AF; margin-bottom:10px;">No comments yet.</div>

                <div v-if="canAct" style="display:flex; gap:6px; align-items:flex-end;">
                  <textarea
                    v-model="commentDrafts[selectedItem.id]"
                    placeholder="Add a comment…"
                    rows="1"
                    style="flex:1; padding:6px 8px; border:1px solid #E5E7EB; border-radius:5px; font-size:12px; color:#374151; resize:vertical; outline:none; line-height:1.4;"
                    @keydown.enter.exact.prevent="postComment(selectedItem)"
                  />
                  <button
                    @click="postComment(selectedItem)"
                    :disabled="!(commentDrafts[selectedItem.id] || '').trim() || postingComment[selectedItem.id]"
                    style="padding:6px 12px; font-size:11px; font-weight:500; color:#fff; background:#D97706; border:none; border-radius:5px; cursor:pointer;"
                    :style="{ opacity: (!(commentDrafts[selectedItem.id] || '').trim() || postingComment[selectedItem.id]) ? 0.5 : 1 }"
                  >Post</button>
                </div>
              </template>
            </div>
          </div>
        </template>

        <div v-else style="display:flex; align-items:center; justify-content:center; height:100%; color:#A9A29A; font-size:12px;">
          Select a focus item to view details
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

const FOCUS_ITEM_TYPE_ID = 40
// Grey is reserved for the archived state (see ARCHIVED_COLOR below) — Low
// priority moved to blue so the two are never visually confused.
const PRIORITY_COLORS = { p0: '#DC2626', p1: '#EA580C', p2: '#CA8A04', p3: '#60A5FA', none: '#60A5FA' }
const PRIORITY_LABELS = { p0: 'Critical', p1: 'High', p2: 'Medium', p3: 'Low', none: '' }
const ARCHIVED_COLOR = '#9CA3AF'

export default {
  name: 'ExternalFocusItems',
  props: {
    entityId: { type: Number, default: null },
    // When true, the component renders WITHOUT its own border/padding — the
    // host (ConfigurablePage panel on /engage) already provides the chrome, so
    // this removes the double-nesting buffer. Standalone (/external) keeps its
    // own bordered card. Passed via section settings on Engage.
    embedded: { type: Boolean, default: false },
    // Optional header title rendered INSIDE the component's own header row
    // (title left, action buttons right) — used on /engage so the buttons live
    // in the "Focus Areas" title bar as one flush unit. When empty, the host
    // page supplies the header and only the buttons render.
    title: { type: String, default: '' },
  },

  data() {
    return {
      items: [],
      loadingItems: false,
      saving: false,
      showCreate: false,
      showArchived: false,
      selectedId: null,
      editingId: null,
      newItem: { title: '', description: '', priority: 'p1', entity: null },
      editForm: { title: '', description: '', priority: 'p1', entity: null },
      childEntities: [],
      currentEntity: null,
      commentsByItem: {},        // itemId -> [comments]
      commentDrafts: {},        // itemId -> draft string
      commentLoading: {},       // itemId -> bool
      postingComment: {},       // itemId -> bool
    }
  },

  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['isTenantAdmin', 'current_tenant_roles']),

    // Inner panel (list/detail, loading, empty) sits edge-to-edge. Standalone,
    // negative margins cancel the wrapper's 12/14px padding. Embedded, the
    // wrapper has no padding so no negative margins are needed.
    innerPanelMargin() {
      return this.embedded ? '0' : '0 -14px -12px'
    },

    // Full management: create / edit title-priority-entity / delete. Admins only.
    canManage() {
      return this.current_user?.is_superuser || this.isTenantAdmin
    },

    // Acknowledge: mark complete / not relevant + comment. Any portal user
    // who isn't view-only (readonly). Restricted/standard/portal included.
    canAct() {
      if (this.canManage) return true
      const roles = this.current_tenant_roles || []
      const actor = ['restricted', 'standard', 'portal', 'manager_viewer', 'security', 'admin']
      return roles.some(r => actor.includes(r))
    },

    // Backwards-compat alias for the management gate.
    canEdit() {
      return this.canManage
    },

    // Only Admins (canManage) may view archived items — standard/portal/
    // restricted users never see them, even via the toggle (it's hidden
    // for them). Server-side, get_queryset() also excludes 'archived' for
    // non-admins, so this is a UI convenience on top of a real guard, not
    // the only guard.
    canViewArchived() {
      return this.canManage
    },

    scopedItems() {
      if (!this.entityId) return this.items
      return this.items.filter(item => {
        if (!item.entity) return true
        return item.entity === this.entityId || this.isDescendantEntity(item.entity)
      })
    },

    visibleItems() {
      const list = this.showArchived ? this.archivedItems : this.openItems
      return list
    },

    openItems() {
      // Show open AND completed items (archived stays in its own view).
      // Completed items sink to the BOTTOM, de-emphasized. Within each group,
      // sort by priority.
      const po = { p0: 0, p1: 1, p2: 2, p3: 3, none: 4 }
      return this.scopedItems
        .filter(i => i.status !== 'archived')
        .sort((a, b) => {
          const ac = this.isCompleted(a) ? 1 : 0
          const bc = this.isCompleted(b) ? 1 : 0
          if (ac !== bc) return ac - bc               // open group first, completed last
          return (po[a.priority] ?? 4) - (po[b.priority] ?? 4)
        })
    },

    archivedItems() {
      return this.scopedItems
        .filter(i => i.status === 'archived')
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
    },

    selectedItem() {
      return this.visibleItems.find(i => i.id === this.selectedId) || null
    },
  },

  watch: {
    entityId: {
      immediate: true,
      handler() {
        this.fetchItems()
        this.fetchCurrentEntity()
        this.fetchChildEntities()
      },
    },
    visibleItems(list) {
      // Keep a selection alive if possible; otherwise default to the first row.
      if (this.selectedId && list.some(i => i.id === this.selectedId)) return
      this.selectedId = list.length ? list[0].id : null
    },
  },

  methods: {
    // Archived always renders grey regardless of priority — grey is reserved
    // for that state so it can't be confused with Low priority (blue).
    priorityColor(p, status) {
      if (status === 'archived') return ARCHIVED_COLOR
      return PRIORITY_COLORS[p] || PRIORITY_COLORS.none
    },
    priorityLabel(p) { return PRIORITY_LABELS[p] || '' },

    // A focus item that's been resolved/dismissed (done, resolved, closed, or
    // not relevant). These sink to the bottom of the list, de-emphasized.
    isCompleted(item) {
      return ['done', 'resolved', 'closed', 'not_relevant'].includes(item.status)
    },

    // Comment count comes from the list API (serializer comment_count, annotated
    // — no per-row fetch). Fall back to any lazily-fetched thread we already have.
    itemCommentCount(item) {
      if (typeof item.comment_count === 'number') return item.comment_count
      const c = this.commentsByItem[item.id]
      return Array.isArray(c) ? c.length : 0
    },

    isOverdue(item) {
      if (!item.due_date) return false
      return new Date(item.due_date) < new Date(new Date().toDateString())
    },

    formatDate(d) {
      if (!d) return ''
      const dt = new Date(d + 'T00:00:00')
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },

    isDescendantEntity(itemEntityId) {
      return this.childEntities.some(e => e.id === itemEntityId)
    },

    selectItem(item) {
      this.selectedId = item.id
      this.editingId = null
      if (this.commentsByItem[item.id] === undefined) {
        this.fetchComments(item)
      }
    },

    // Image attachments on a focus item (the captured screenshots). The
    // serializer returns all media on `attachments`; filter to images.
    imageAttachments(item) {
      const list = item?.attachments || []
      return list.filter(a => (a.file_type || '').startsWith('image/') ||
        /\.(png|jpe?g|gif)$/i.test(a.filename || ''))
    },

    // Attachments are private S3 objects — fetch a short-lived presigned URL
    // via the existing download action, then open it in a new tab.
    async openAttachment(a) {
      try {
        const resp = await this.$axios.get(`/api/requirement-media/${a.id}/download/`)
        const url = resp.data?.url
        if (url) window.open(url, '_blank', 'noopener')
      } catch (err) {
        console.error('Failed to open attachment:', err)
      }
    },

    async fetchItems() {
      this.loadingItems = true
      try {
        // tenant_scoped forces the backend to hard-filter to the viewed tenant
        // even for superusers (the Support viewset is cross-tenant by default).
        // This is a customer-facing surface — Focus Items must never leak across
        // tenants.
        const resp = await this.$axios.get('/api/requirements/', {
          params: { requirement_type: FOCUS_ITEM_TYPE_ID, tenant_scoped: true, page_size: 100 },
        })
        this.items = resp.data?.results || resp.data || []
      } catch (err) {
        console.error('Failed to fetch focus items:', err)
        this.items = []
      } finally {
        this.loadingItems = false
      }
    },

    async fetchCurrentEntity() {
      if (!this.entityId) {
        this.currentEntity = null
        return
      }
      try {
        const resp = await this.$axios.get(`/api/entities/${this.entityId}/`)
        const e = resp.data || {}
        this.currentEntity = { id: e.id, name: e.name }
        // Default new items to the currently-selected entity (the practice),
        // not ACO-wide.
        if (this.newItem.entity == null) this.newItem.entity = e.id
      } catch (err) {
        console.error('Failed to fetch current entity:', err)
        this.currentEntity = null
      }
    },

    async fetchChildEntities() {
      if (!this.entityId) {
        this.childEntities = []
        return
      }
      try {
        const resp = await this.$axios.get('/api/entities/', {
          params: { ancestor_ids: this.entityId, page_size: 2000 },
        })
        const results = resp.data?.results || resp.data || []
        this.childEntities = results
          .map(e => ({ id: e.id, name: e.name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      } catch (err) {
        console.error('Failed to fetch child entities:', err)
        this.childEntities = []
      }
    },

    async createItem() {
      if (!this.newItem.title.trim() || this.saving) return
      this.saving = true
      try {
        const resp = await this.$axios.post('/api/requirements/', {
          requirement_type: FOCUS_ITEM_TYPE_ID,
          title: this.newItem.title.trim(),
          description: this.newItem.description.trim(),
          priority: this.newItem.priority,
          entity: this.newItem.entity,
        })
        this.cancelCreate()
        await this.fetchItems()
        if (resp.data?.id) this.selectedId = resp.data.id
      } catch (err) {
        console.error('Failed to create focus item:', err)
      } finally {
        this.saving = false
      }
    },

    cancelCreate() {
      this.showCreate = false
      this.newItem = {
        title: '', description: '', priority: 'p1',
        entity: this.currentEntity ? this.currentEntity.id : null,
      }
    },

    startEdit(item) {
      this.editingId = item.id
      this.editForm = {
        title: item.title,
        description: item.description || '',
        priority: item.priority,
        entity: item.entity,
      }
    },

    cancelEdit() {
      this.editingId = null
    },

    async saveEdit() {
      if (!this.editForm.title.trim() || this.saving) return
      this.saving = true
      try {
        await this.$axios.patch(`/api/requirements/${this.editingId}/`, {
          title: this.editForm.title.trim(),
          description: this.editForm.description.trim(),
          priority: this.editForm.priority,
          entity: this.editForm.entity,
        })
        this.editingId = null
        await this.fetchItems()
      } catch (err) {
        console.error('Failed to update focus item:', err)
      } finally {
        this.saving = false
      }
    },

    async setStatus(item, status) {
      try {
        await this.$axios.patch(`/api/requirements/${item.id}/set-status/`, { status })
        await this.fetchItems()
      } catch (err) {
        console.error('Failed to set status:', err)
      }
    },

    markDone(item) {
      return this.setStatus(item, 'done')
    },

    markNotRelevant(item) {
      return this.setStatus(item, 'not_relevant')
    },

    // Admin-only (enforced server-side too — set_status rejects 'archived'
    // for non-admins, and get_queryset hides archived rows from non-admins).
    archiveItem(item) {
      return this.setStatus(item, 'archived')
    },

    unarchiveItem(item) {
      // Reopen — archiving isn't "done", it's "hidden", so restoring it
      // should land back in the active open list, not a done bucket.
      return this.setStatus(item, 'open')
    },

    toggleArchivedView() {
      this.showArchived = !this.showArchived
      this.selectedId = null
    },

    async fetchComments(item) {
      this.commentLoading = { ...this.commentLoading, [item.id]: true }
      try {
        const resp = await this.$axios.get('/api/requirement-comments/', {
          params: { requirement: item.id, page_size: 200 },
        })
        const data = resp.data?.results || resp.data || []
        this.commentsByItem = { ...this.commentsByItem, [item.id]: data }
      } catch (err) {
        console.error('Failed to fetch comments:', err)
        this.commentsByItem = { ...this.commentsByItem, [item.id]: [] }
      } finally {
        this.commentLoading = { ...this.commentLoading, [item.id]: false }
      }
    },

    async postComment(item) {
      const body = (this.commentDrafts[item.id] || '').trim()
      if (!body || this.postingComment[item.id]) return
      this.postingComment = { ...this.postingComment, [item.id]: true }
      try {
        await this.$axios.post('/api/requirement-comments/', {
          requirement: item.id,
          body,
        })
        this.commentDrafts = { ...this.commentDrafts, [item.id]: '' }
        await this.fetchComments(item)
      } catch (err) {
        console.error('Failed to post comment:', err)
      } finally {
        this.postingComment = { ...this.postingComment, [item.id]: false }
      }
    },

    commentCount(item) {
      const c = this.commentsByItem[item.id]
      return Array.isArray(c) ? c.length : null
    },

    commentAuthor(c) {
      return c.author_name || c.author?.name || c.email_sender || 'Unknown'
    },

    formatCommentTime(c) {
      if (!c.created_at) return ''
      const dt = new Date(c.created_at)
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
        ' ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    },

    async deleteItem(item) {
      if (!confirm(`Remove "${item.title}"?`)) return
      try {
        await this.$axios.delete(`/api/requirements/${item.id}/`)
        if (this.selectedId === item.id) this.selectedId = null
        await this.fetchItems()
      } catch (err) {
        console.error('Failed to delete focus item:', err)
      }
    },
  },
}
</script>

<style scoped>
.focus-list-row {
  padding: 10px 14px;
  border-bottom: 1px solid #F2EEE8;
  cursor: pointer;
  transition: background 0.15s;
}
.focus-list-row:hover {
  background: #FAFAF8;
}
.focus-list-row--active {
  background: #FFFBF5;
  box-shadow: inset 3px 0 0 #D97706;
}
.focus-list-row--done {
  background: #FAFAF9;
}
.focus-list-row--done:hover {
  background: #F4F4F2;
}
.focus-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #6B7280;
  transition: background 0.15s;
}
.focus-icon-btn:hover {
  background: #F3F4F6;
}
.focus-shot-thumb {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: #FAFAF8;
  color: #9CA3AF;
  cursor: pointer;
  transition: all 0.15s;
}
.focus-shot-thumb:hover {
  border-color: #D97706;
  background: #FFFBF5;
  color: #D97706;
}
</style>
